import type { ReactElement, ReactNode } from "react"
import { useContext, useEffect } from "react"
import { graphql } from "gatsby"
import ParallaxWrapperOrg from "@onestore/hel/dist/components/organisms/ParallaxWrapperOrg"
import ConditionalWrapper from "@onestore/hel/dist/helpers/ConditionalWrapper"
import DomainExtensionSchema from "@gatsby-plugin-generic-page/components/DomainExtensionSchema"
import Section from "@gatsby-plugin-generic-page/components/Section"
import useGenericPageBreadcrumbs from "@gatsby-plugin-generic-page/hooks/useGenericPageBreadcrumbs"
import { useIsomorphicLayoutEffect } from "@gatsby-plugin-generic-page/hooks/useIsomorphicLayoutEffect"
import { SectionType } from "@gatsby-plugin-generic-page/types/sectionType"
import { typeMatches } from "@onestore-graphql"
import type { PlaceholderSection } from "@onestore-graphql/Sections"
import { useLocation } from "@reach/router"
import ExternalScripts from "~/components/ExternalScripts"
import Metadata from "~/components/Metadata"
import { BreadcrumbsContextProvider } from "~/context/BreadcrumbsContext"
import HasFullScreenContainerContext from "~/context/HasFullScreenContainerContext"
import { SwitchContentContextProvider } from "~/context/SwitchContentContext"
import isEmpty from "~/lib/isEmpty"
import { setGenericPageAnalytics } from "~/lib/pageAnalytics"
import Storage from "~/lib/storage"
import { setBackToShopUrl } from "~/store/bonus/actions-mini-cart"
import { useAppDispatch } from "~/store/hooks"
import type { GenericPage as SquidexGenericPage } from "~/types/squidex"
import SquidexA11yToolbar from "../../../gatsby-plugin-squidex/src/components/SquidexA11yToolbar"
import SquidexToolbarSectionWrapper from "../../../gatsby-plugin-squidex/src/components/SquidexToolbarSectionWrapper"

interface GenericPageRendererProps {
  page: SquidexGenericPage
  onSectionRender?: (sectionId: string) => ReactNode
}

export function GenericPageRenderer({
  page,
  onSectionRender,
}: GenericPageRendererProps): ReactElement<GenericPageProps> {
  const { sections, url, productArea, productSection } = page.flatData
  const isHomepage = url === "/"
  const breadcrumbs = useGenericPageBreadcrumbs(url)
  const dispatch = useAppDispatch()
  const { pathname } = useLocation() as Window["location"]

  // Hands the page_view parameters over to onRouteUpdate (ONESTORE-298).
  // Must be a layout effect — see the note in ~/lib/pageAnalytics.
  //
  // Keyed by the path the page is served at, not by page.flatData.url: this
  // renderer is also used for experiment variants and terms pages, whose
  // content url differs from the url the visitor is on.
  useIsomorphicLayoutEffect()(() => {
    setGenericPageAnalytics({
      url: pathname,
      productArea: productArea ?? null,
      productSection: productSection ?? null,
    })
  }, [pathname, productArea, productSection])

  useEffect(() => {
    // Any main-shop page is the counterpart to cp-shop's ShopPage (which sets
    // this unconditionally too) — see getBackToShopUrl, used as the basket's
    // "back to shop" link target. Always points at the shop root, not the
    // current page, mirroring ShopPage pointing at the cp-shop root.
    dispatch(setBackToShopUrl("/"))
    Storage.setBackToShopUrl("/")
  }, [dispatch])

  return (
    <>
      <ExternalScripts page={page} />

      <Metadata
        metadata={page}
        isHomepage={isHomepage}
        noIndex={page.flatData.noIndex}
      />

      <DomainExtensionSchema />

      <SwitchContentContextProvider>
        <ConditionalWrapper
          condition={!isHomepage}
          wrapper={(children) => (
            <BreadcrumbsContextProvider
              homeUrl="/"
              items={breadcrumbs.items}
              currentLabel={breadcrumbs.currentLabel}
            >
              {children}
            </BreadcrumbsContextProvider>
          )}
        >
          <GenericPageSectionsRenderer
            pageId={page.id}
            sections={sections}
            onSectionRender={onSectionRender}
          />
        </ConditionalWrapper>
      </SwitchContentContextProvider>
    </>
  )
}

interface SectionsRendererProps {
  pageId: string
  sections: SquidexGenericPage["flatData"]["sections"] | null
  onSectionRender?: (sectionId: string) => ReactNode
}

export function GenericPageSectionsRenderer({
  sections,
  pageId,
  onSectionRender,
}: SectionsRendererProps) {
  const hasFullScreenContainer = useContext(HasFullScreenContainerContext)

  if (isEmpty(sections)) {
    return null
  }

  const hasParallaxSection = sections.some((section) =>
    typeMatches(section.__typename, SectionType.PARALLAX_SECTION)
  )

  return (
    <ConditionalWrapper
      condition={hasParallaxSection ? hasFullScreenContainer : false}
      wrapper={(children) => (
        <ParallaxWrapperOrg>{children}</ParallaxWrapperOrg>
      )}
    >
      {sections.map((section) => {
        if (
          typeMatches(section.__typename, SectionType.PLACEHOLDER_SECTION) &&
          onSectionRender
        ) {
          const { flatData, url, id } = section as PlaceholderSection

          return (
            <SquidexToolbarSectionWrapper urlToSection={url} key={id}>
              {onSectionRender(flatData.id)}
            </SquidexToolbarSectionWrapper>
          )
        }

        return (
          <SquidexToolbarSectionWrapper
            urlToSection={section.url}
            /** key with page ID needed when we perform A/B test with the same section on either page */
            key={`${pageId}-${section.id}`}
          >
            <Section data={section} />
          </SquidexToolbarSectionWrapper>
        )
      })}
    </ConditionalWrapper>
  )
}

interface GenericPageProps {
  data: {
    GraphCmsSquidex: {
      page: SquidexGenericPage
    }
  }
}

interface PartialGenericPageRendererProps {
  page: SquidexGenericPage
  sectionTypes: SectionType[]
}

export function PartialGenericPageRenderer({
  page,
  sectionTypes,
}: PartialGenericPageRendererProps): ReactElement<PartialGenericPageRendererProps> {
  const { sections } = page.flatData

  return (
    <GenericPageSectionsRenderer
      pageId={page.id}
      sections={sections.filter((section) =>
        sectionTypes.some((type) => typeMatches(section.__typename, type))
      )}
    />
  )
}

export default function GenericPage({
  data: {
    GraphCmsSquidex: { page },
  },
}: GenericPageProps): ReactElement<GenericPageProps> {
  return (
    <>
      <GenericPageRenderer page={page} />
      <SquidexA11yToolbar page={page} />
    </>
  )
}

export const query = graphql`
  query ($genericPageId: String!) {
    GraphCmsSquidex {
      page: findGenericPageContent(id: $genericPageId) {
        ...GenericPage
      }
    }
  }
`
